import { get, pick } from "lodash";

import { thor } from "../../typings/thor";
import { Enums } from "../../thor.enums";
import {
    COMPANY,
    CONTACT_EMAIL,
    CONTACT_EMAIL_BUSINESS,
    DOMAIN,
    EXTENSION_DOWNLOAD_PATH,
    SITE_NAME
} from "../../thor.const";

// tslint:disable:max-line-length
const GTU_URL = {
    [Enums.Site.Auction]: "https://static.interencheres.com/AUCTION/Legal/Clients/VF_UK+Conditions+G%C3%A9n%C3%A9rales++de+Partenariat+AUCTION.pdf",
    [Enums.Site.Interencheres]: "https://static.interencheres.com/v4/legal/AA/CG_AA_IE.pdf"
};

const PRICING_CONDITIONS_URL = {
    [Enums.Site.Auction]: "https://static.interencheres.com/AUCTION/Legal/Clients/VF_UK+Conditions+tarifaires-+Clients+Auction.pdf",
    [Enums.Site.Interencheres]: "https://static.interencheres.com/v4/legal/AA/CT_AA_LIVE_IE.pdf"
};
// tslint:enable:max-line-length

class SitesService {
    public static $inject: string[] = [
        "$window",
        "configService"
    ];

    public company: { address: string; name: string; rcs: string };
    public contactPhone: string;
    public contactPhoneInter: string;
    public contactEmail: string;
    public contactEmailBusiness: string;
    public contactSupport: {
        email: string;
        tel: string;
        tel_inter: string;
        tel_link: string;
        email_link: string;
    };

    public ieDomain: string;
    public auctionDomain: string;

    public site: Enums.Site;
    public siteName: string;
    public auctionSiteName: string;
    public siteDomain: string;

    public extension: {
        id?: string;
        download?: string;
        majorVersion?: string;
    } = {};

    public gtuUrl: string;
    public auctionGtuUrl: string;
    public pricingConditionsUrl: string;

    constructor(
        private $window: ng.IWindowService,
        configService
    ) {
        this.ieDomain = configService.ie.domain;
        this.auctionDomain = configService.auction.domain;

        this.site = this.isAuctionDomain() ? Enums.Site.Auction : Enums.Site.Interencheres;
        const siteKey = this.isAuctionDomain() ? "auction" : "ie";

        this.siteName = SITE_NAME[this.site];
        this.auctionSiteName = SITE_NAME[Enums.Site.Auction];
        this.siteDomain = DOMAIN[this.site];
        this.company = COMPANY;
        this.contactPhone = configService[siteKey]?.support?.phoneNumber;
        this.contactPhoneInter = configService[siteKey]?.support?.phoneNumberInter;
        this.contactEmail = CONTACT_EMAIL[this.site];
        this.contactSupport = {
            email: this.contactEmail,
            tel: this.contactPhone,
            tel_inter: this.contactPhoneInter,
            tel_link: `tel:${(this.contactPhone || "").replace(/\s/g, "")}`,
            email_link: `mailto:${(this.contactEmail || "")}`
        };
        this.contactEmailBusiness = CONTACT_EMAIL_BUSINESS[this.site];

        this.extension.id = get(configService, `${siteKey}.extension.id`);
        this.extension.majorVersion = get(configService, `${siteKey}.extension.majorVersion`);
        this.extension.download = EXTENSION_DOWNLOAD_PATH + this.extension.id;

        this.gtuUrl = GTU_URL[this.site];
        this.auctionGtuUrl = GTU_URL[Enums.Site.Auction];
        this.pricingConditionsUrl = PRICING_CONDITIONS_URL[this.site];
    }

    // check where organization can publishing by site

    public organizationCanPublishToInterencheres(organization: thor.Organization): boolean {
        return this.organizationCanPublish(organization, Enums.Site.Interencheres);
    }

    public organizationCanPublishToAuction(organization: thor.Organization): boolean {
        return this.organizationCanPublish(organization, Enums.Site.Auction);
    }

    public isOrganizationDuo(organization: thor.Organization): boolean {
        return this.organizationCanPublishToAuction(organization) &&
            this.organizationCanPublishToInterencheres(organization);
    }

    // check which atlas by domain

    public isAuctionDomain(): boolean {
        return this.$window.location.host === this.auctionDomain;
    }

    public isIeDomain(): boolean {
        return this.$window.location.host === this.ieDomain;
    }

    // check where sale is published by site

    public isAuction(sites: thor.SaleSites): boolean {
        return this.isAuthorizedSite(sites, Enums.Site.Auction);
    }

    public isIE(sites: thor.SaleSites): boolean {
        return this.isAuthorizedSite(sites, Enums.Site.Interencheres);
    }

    // check where sale is published Live by site

    public isAuctionLive(sites: thor.SaleSites): boolean {
        return this.isAuthorizedSite(sites, Enums.Site.Auction, Enums.AuctionType.Live);
    }

    public isLive(sites: thor.SaleSites): boolean {
        return this.isAuthorizedSite(sites, Enums.Site.Interencheres, Enums.AuctionType.Live);
    }

    public isOnline(sites: thor.SaleSites): boolean {
        return this.isAuthorizedSite(sites, Enums.Site.Interencheres, Enums.AuctionType.Online);
    }

    public isSiteLive(sites: thor.SaleSites) {
        return this.isLive(sites) || this.isAuctionLive(sites);
    }

    private organizationCanPublish(
        organization: thor.Organization,
        site: Enums.Site
    ): boolean {
        return get(organization, "authorized_sites", <Enums.Site[]>[]).includes(site);
    }

    private isAuthorizedSite(
        sites: thor.SaleSites,
        site: Enums.Site,
        auctionType: Enums.AuctionType = undefined
    ): boolean {
        if (!sites || !sites[site]) {
            return false;
        }
        const { is_present: isPresent, type } = pick(sites[site], ["is_present", "type"]);
        return auctionType ? isPresent && type === auctionType : isPresent;
    }
}

angular
    .module("thor.core.sites.service", [])
    .service("sitesService", SitesService);
